import { ApiError } from "./errors";
import type { ApiClientConfig, HttpMethod, RequestOptions } from "./types";
import type { ApiResponse } from "@/shared/types/api";
import { captureError, logger } from "@/shared/lib/logger";

const DEFAULT_TIMEOUT_MS = 15000;

const buildUrl = (baseUrl: string, path: string): string => {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (!baseUrl) return path;
  const trimmedBase = baseUrl.replace(/\/$/, "");
  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
};

const buildHeaders = (
  config: ApiClientConfig,
  init: RequestOptions | undefined,
  body: unknown,
  authToken: string | null | undefined,
): Headers => {
  const headers = new Headers(config.defaultHeaders);
  if (authToken) headers.set("Authorization", `Bearer ${authToken}`);

  if (body !== undefined && !(body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (init?.headers) {
    const incoming = new Headers(init.headers as HeadersInit);
    incoming.forEach((value, key) => headers.set(key, value));
  }
  return headers;
};

const parseResponseBody = async <T>(response: Response): Promise<ApiResponse<T> | T | null> => {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as ApiResponse<T> | T;
  }
  return (await response.text()) as unknown as T;
};

const isApiResponse = <T>(value: unknown): value is ApiResponse<T> =>
  typeof value === "object" && value !== null && "success" in value && "data" in value;

interface InternalRequest {
  method: HttpMethod;
  path: string;
  body?: unknown;
  options?: RequestOptions;
}

/**
 * 단일 네트워크 전송. timeout/abort 처리와 network/timeout ApiError 변환을 담당한다.
 * 401 재시도 시 동일 인자로 두 번 호출될 수 있도록 부수효과 없는 순수 전송으로 분리했다.
 */
const executeFetch = async (
  config: ApiClientConfig,
  url: string,
  method: HttpMethod,
  headers: Headers,
  serializedBody: BodyInit | undefined,
  options: RequestOptions | undefined,
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? config.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutId =
    timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

  // Combine external signal with our timeout controller
  if (options?.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  try {
    return await fetch(url, {
      ...options,
      method,
      headers,
      body: serializedBody,
      signal: controller.signal,
    });
  } catch (error) {
    // 캡처는 request 래퍼의 단일 지점에서 일괄 처리한다(모든 kind 통합) — 여기선 변환·throw 만.
    if (controller.signal.aborted && !options?.signal?.aborted) {
      throw new ApiError("요청 시간이 초과되었습니다.", { kind: "timeout", cause: error });
    }
    throw new ApiError("네트워크 오류가 발생했습니다.", { kind: "network", cause: error });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const executeRequest = async <T>(
  config: ApiClientConfig,
  req: InternalRequest,
): Promise<ApiResponse<T>> => {
  const { method, path, body, options } = req;
  const url = buildUrl(config.baseUrl, path);

  let serializedBody: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) serializedBody = body;
    else if (method !== "GET") serializedBody = JSON.stringify(body);
  }

  // preflight: 인증 client 는 만료 임박 시 사전 갱신된 토큰을 받는다.
  const authToken = config.prepareAuthToken
    ? await config.prepareAuthToken()
    : (config.getAuthToken?.() ?? null);

  let response = await executeFetch(
    config,
    url,
    method,
    buildHeaders(config, options, body, authToken),
    serializedBody,
    options,
  );

  // 401 → refresh 후 원 요청을 단 한 번만 재시도 (refreshAuthToken 이 주입된 인증 client 한정).
  // 공개 client 는 refreshAuthToken 이 없어 /auth/refresh 가 자기 자신을 재귀 호출하지 않는다.
  if (response.status === 401 && config.refreshAuthToken) {
    const refreshedToken = await config.refreshAuthToken(Boolean(authToken));
    response = await executeFetch(
      config,
      url,
      method,
      buildHeaders(config, options, body, refreshedToken),
      serializedBody,
      options,
    );
  }

  // refresh 재시도까지 거친 뒤에도 401 이면 세션 자체가 서버에서 무효 — 후속 복구를 위임한다.
  // (refresh 가 throw 한 경우는 이 지점에 도달하지 않는다 — coordinator/기존 정책이 담당)
  // ⚠️ 아래 onError(계정상태 USER_WITHDRAWN·USER_SUSPENDED·USER_BLOCKED 인터셉트)와의 이중 발화 방지는
  //    "계정상태=400 / 순수 세션무효=401" 서버 계약의 status 분리에 의존한다. 여기선 status 로만
  //    게이팅하므로, 계약이 드리프트해 계정상태 코드가 401 로 오면 두 훅이 함께 emit 한다
  //    (현재 계약에선 미발생 — 필요 시 errorCode 인지형 가드로 상호배타화).
  if (response.status === 401 && config.onAuthSessionRejected) {
    config.onAuthSessionRejected();
  }

  let parsed: ApiResponse<T> | T | null;
  try {
    parsed = await parseResponseBody<T>(response);
  } catch (error) {
    throw new ApiError("응답을 파싱하지 못했습니다.", {
      kind: "unknown",
      status: response.status,
      cause: error,
    });
  }

  if (!response.ok) {
    const record =
      parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : undefined;
    const message =
      (isApiResponse(parsed) && parsed.message) ||
      (record && "message" in record ? String(record.message) : `HTTP ${response.status}`);
    // 백엔드는 errorCode 를 사용한다. 레거시 응답은 code 를 쓰므로 fallback.
    const errorCode =
      record && typeof record.errorCode === "string"
        ? record.errorCode
        : record && typeof record.code === "string"
          ? record.code
          : undefined;
    const errors =
      record && typeof record.errors === "object" && record.errors !== null
        ? (record.errors as Record<string, string | string[]>)
        : undefined;
    // detail: 탈퇴/정지/차단 등 일부 errorCode 에만 채워지는 구조화 메타. null 은 유효값(USER_BLOCKED 블록리스트).
    const detail =
      record && "detail" in record && typeof record.detail === "object"
        ? (record.detail as Record<string, unknown> | null)
        : undefined;
    const httpError = new ApiError(message, {
      kind: "http",
      status: response.status,
      code: errorCode,
      errorCode,
      errors,
      detail,
      response: parsed,
    });
    logger.warn("HTTP error", { url, status: response.status, errorCode });
    config.onError?.(httpError);
    throw httpError;
  }

  if (isApiResponse<T>(parsed)) {
    if (parsed.success === false) {
      throw new ApiError(parsed.message || "요청이 실패했습니다.", {
        kind: "business",
        status: response.status,
        response: parsed,
      });
    }
    return parsed;
  }

  // Non-envelope response: wrap into ApiResponse
  return { success: true, data: parsed as T };
};

// 이미 보고한 에러는 재보고하지 않는다 — 실패한 요청당 정확히 1회.
// (auth refresh 는 authPublicApiClient 를 통한 또 하나의 request 라, 중첩 refresh 실패가 refresh 래퍼와
//  원 요청 래퍼에서 이중 캡처될 수 있다. 같은 에러 객체를 rethrow 하므로 WeakSet 으로 흡수한다.)
const reportedErrors = new WeakSet<object>();

// 모든 API 에러(network/timeout/http/business/unknown)를 단일 지점에서 Sentry 로 보고 후 재던진다.
// (uncaught 는 전역/React root 핸들러가 담당 — 여기선 앱이 try/catch 로 받는 handled 에러 전량 수집.)
const request = async <T>(
  config: ApiClientConfig,
  req: InternalRequest,
): Promise<ApiResponse<T>> => {
  try {
    return await executeRequest<T>(config, req);
  } catch (error) {
    if (error && typeof error === "object" && !reportedErrors.has(error)) {
      reportedErrors.add(error);
      const context: Record<string, unknown> = {
        // 쿼리스트링 제거 — 경로에 ?code=<전화번호형 추천코드> 등 PII 가 실릴 수 있어 route 만 보고한다.
        url: buildUrl(config.baseUrl, req.path).split("?")[0],
        method: req.method,
      };
      if (error instanceof ApiError) {
        context.kind = error.kind;
        context.status = error.status;
        context.errorCode = error.errorCode;
      }
      captureError(error, context);
    }
    throw error;
  }
};

export const createApiClient = (config: ApiClientConfig) => ({
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(config, { method: "GET", path, options }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(config, { method: "POST", path, body, options }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(config, { method: "PUT", path, body, options }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(config, { method: "PATCH", path, body, options }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(config, { method: "DELETE", path, options }),
  postForm: <T>(path: string, formData: FormData, options?: RequestOptions) =>
    request<T>(config, { method: "POST", path, body: formData, options }),
});

export type ApiClient = ReturnType<typeof createApiClient>;
